import { useState, useEffect } from "react";
import API from "../lib/api";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import {
  ChartBar, Folder, Briefcase, Users, Truck, CheckCircle, Clock,
  Warning, Lightning, TrendUp, ArrowRight, ArrowCounterClockwise
} from "@phosphor-icons/react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from "recharts";
import { Link } from "react-router-dom";

const PIE_COLORS = ["#10B981", "#3B82F6", "#EF4444", "#6B7280"];

function StatCard({ icon: Icon, label, value, sub, color = "text-primary" }) {
  return (
    <div className="border border-border rounded-lg p-3 bg-card" data-testid={`stat-${label.toLowerCase().replace(/\s/g, "-")}`}>
      <Icon size={16} className={color} weight="duotone" />
      <p className="text-xl font-bold font-[Chivo] mt-1">{value}</p>
      <p className="text-[9px] text-muted-foreground uppercase tracking-wider">{label}</p>
      {sub && <p className="text-[10px] text-muted-foreground mt-0.5">{sub}</p>}
    </div>
  );
}

export default function TenantAnalyticsPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/tenant/analytics")
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load analytics"))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="text-center py-20 text-muted-foreground">Loading analytics...</div>;
  if (!data) return <div className="text-center py-20 text-muted-foreground">Failed to load analytics</div>;

  const { projects, tasks, avg_job_turnaround_days, supplier_performance, completion_trend, emergency_jobs, usage } = data;

  return (
    <div data-testid="tenant-analytics-page">
      <div className="flex items-center gap-3 mb-6">
        <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-violet-600 to-indigo-600 flex items-center justify-center">
          <ChartBar size={18} className="text-white" weight="fill" />
        </div>
        <div>
          <h1 className="text-xl sm:text-2xl font-bold font-[Chivo] tracking-tight">Analytics</h1>
          <p className="text-xs text-muted-foreground">Your workspace performance overview</p>
        </div>
      </div>

      {/* Top KPIs */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mb-6" data-testid="analytics-kpis">
        <StatCard icon={Folder} label="Projects" value={projects.total} sub={`${projects.completion_rate}% complete`} color="text-blue-500" />
        <StatCard icon={CheckCircle} label="Tasks Done" value={tasks.completed} sub={`of ${tasks.total}`} color="text-emerald-500" />
        <StatCard icon={Warning} label="Overdue" value={tasks.overdue} color="text-red-500" />
        <StatCard icon={Clock} label="Avg Turnaround" value={`${avg_job_turnaround_days}d`} sub="Job completion" color="text-amber-500" />
        <StatCard icon={Truck} label="Suppliers" value={usage.vendors} color="text-purple-500" />
        <StatCard icon={Lightning} label="Emergencies" value={emergency_jobs.total} sub={`${emergency_jobs.resolved} resolved`} color="text-red-600" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Completion Trend */}
        <div className="lg:col-span-2 border border-border rounded-lg bg-card p-4" data-testid="completion-trend-chart">
          <h3 className="text-sm font-bold font-[Chivo] mb-3">Project Activity (6 Months)</h3>
          <div className="h-52">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={completion_trend}>
                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                <XAxis dataKey="month" tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                <YAxis allowDecimals={false} tick={{ fontSize: 10 }} className="fill-muted-foreground" />
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid var(--border)" }} />
                <Bar dataKey="created" fill="#3B82F6" name="Created" radius={[4, 4, 0, 0]} />
                <Bar dataKey="completed" fill="#10B981" name="Completed" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Task Status Pie */}
        <div className="border border-border rounded-lg bg-card p-4" data-testid="task-status-chart">
          <h3 className="text-sm font-bold font-[Chivo] mb-3">Task Status</h3>
          <div className="h-40">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie data={tasks.status_breakdown} dataKey="count" nameKey="status" cx="50%" cy="50%" outerRadius={60} strokeWidth={2}>
                  {tasks.status_breakdown.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ fontSize: 11, borderRadius: 8, border: "1px solid var(--border)" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="flex flex-wrap gap-2 justify-center mt-1">
            {tasks.status_breakdown.map((s, i) => (
              <div key={s.status} className="flex items-center gap-1.5 text-[10px]">
                <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: PIE_COLORS[i % PIE_COLORS.length] }} />
                <span className="text-muted-foreground">{s.status} ({s.count})</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Project Breakdown */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6" data-testid="project-breakdown">
        {[
          { label: "Completed", value: projects.completed, color: "text-emerald-500" },
          { label: "In Progress", value: projects.in_progress, color: "text-blue-500" },
          { label: "On Hold", value: projects.on_hold, color: "text-amber-500" },
          { label: "Planned", value: projects.planned, color: "text-gray-500" },
          { label: "Completion Rate", value: `${projects.completion_rate}%`, color: "text-indigo-500" },
        ].map(p => (
          <div key={p.label} className="border border-border rounded-md p-3 bg-card text-center">
            <p className={`text-lg font-bold font-[Chivo] ${p.color}`}>{p.value}</p>
            <p className="text-[9px] text-muted-foreground uppercase">{p.label}</p>
          </div>
        ))}
      </div>

      {/* Supplier Performance */}
      {supplier_performance.length > 0 && (
        <div className="border border-border rounded-lg overflow-hidden" data-testid="supplier-performance">
          <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center gap-2">
            <Truck size={14} className="text-muted-foreground" />
            <h3 className="text-sm font-semibold font-[Chivo]">Supplier Performance</h3>
          </div>
          <div className="divide-y divide-border">
            {supplier_performance.map(s => (
              <div key={s.vendor_id} className="flex items-center justify-between px-4 py-3" data-testid={`supplier-perf-${s.vendor_id}`}>
                <div>
                  <p className="text-xs font-semibold">{s.company_name}</p>
                  <p className="text-[10px] text-muted-foreground">
                    {s.total_tasks} tasks &middot; {s.completed} done &middot; {s.overdue} overdue
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Progress value={s.completion_rate} className="w-20 h-1.5" />
                  <Badge className={`text-[9px] ${s.completion_rate >= 80 ? "bg-emerald-100 text-emerald-700" : s.completion_rate >= 50 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
                    {s.completion_rate}%
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Reports & Audits — Phase 287.3 */}
      <div className="mt-6 border border-border rounded-lg bg-card p-4" data-testid="audit-links">
        <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-semibold mb-3">Reports & Audits</p>
        <Link to="/job-reopen-audit" className="flex items-center justify-between gap-3 p-3 rounded-md hover:bg-muted/40 transition-colors group" data-testid="link-job-reopen-audit">
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 rounded-md bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shrink-0">
              <ArrowCounterClockwise size={15} className="text-white" weight="bold" />
            </div>
            <div>
              <p className="text-sm font-semibold">Job Reopen Audit</p>
              <p className="text-[11px] text-muted-foreground">See who reopened completed jobs and how often — spot accidental completions early.</p>
            </div>
          </div>
          <ArrowRight size={14} className="text-muted-foreground group-hover:translate-x-0.5 transition-transform" />
        </Link>
      </div>
    </div>
  );
}
